import React, { useContext, useEffect, useState } from "react";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import { callAPI } from "../helper/apiUtils";
import { $crud } from "../helper/CrudFactory";
import ProjectPicker from "./ProjectPicker";
import ProjectConfirmModal from "./ProjectConfirmModal";
import { findExactProject, findCloseProject } from "../utils/projectMatch";
import "./EmailClassificationRules.css";

/**
 * EmailClassificationRules
 *
 * Lightweight management UI (shown in the email settings area) for:
 *   - per-account default project ("always associate this account's emails")
 *   - the SPAM/Advertising blocklist
 *
 * Sender→project rules are intentionally not surfaced here: every incoming
 * email is re-classified on its own, so the only "always" behaviour is the
 * per-account default project (explained via the info button).
 */
export default function EmailClassificationRules() {
  const { strings } = useContext(LanguageContext);
  const [data, setData] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [picker, setPicker] = useState(null); // { accountId, query, selectedBuildingId }
  const [confirm, setConfirm] = useState(null); // { suggestion }
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await callAPI(`/email/classification/sender-rules`, {}, "GET");
    const d = res?.data || res;
    if (d && d.status) setData(d);
  };

  useEffect(() => {
    if (!expanded || data) return;
    load();
    (async () => {
      const res = await callAPI(`/email/classification/buildings`, {}, "GET");
      const d = res?.data || res;
      const list = d?.buildings && Array.isArray(d.buildings) ? d.buildings : [];
      setBuildings(list.map((b) => ({ id: b.id, name: b.name })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);

  const apply = async (body) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await callAPI(`/email/classification/sender-rules`, {}, "PUT", body);
      const d = res?.data || res;
      if (d && d.status) {
        await load();
        // A new project may have been created server-side; refresh the list.
        const res2 = await callAPI(`/email/classification/buildings`, {}, "GET");
        const d2 = res2?.data || res2;
        const list = d2?.buildings && Array.isArray(d2.buildings) ? d2.buildings : [];
        setBuildings(list.map((b) => ({ id: b.id, name: b.name })));
      } else {
        $crud.notify({ type: "error", message: (d && d.message) || strings.something_went_wrong || "Something went wrong" });
      }
    } finally {
      setBusy(false);
    }
  };

  // Persist the account default from the picker: existing project by id, or a
  // brand-new (external) project by name.
  const setAccountDefault = (accountId, { buildingId, buildingName }) => {
    apply({ action: "setAccountDefault", accountId, buildingId: buildingId || undefined, buildingName: buildingName || undefined });
    setPicker(null);
    setConfirm(null);
  };

  const submitPicker = () => {
    if (!picker) return;
    const name = (picker.query || "").trim();
    if (picker.selectedBuildingId) {
      setAccountDefault(picker.accountId, { buildingId: picker.selectedBuildingId });
      return;
    }
    if (!name) return;
    const exact = findExactProject(name, buildings);
    if (exact) {
      setAccountDefault(picker.accountId, { buildingId: exact.id });
      return;
    }
    const close = findCloseProject(name, buildings);
    if (close) {
      setConfirm({ suggestion: close });
      return;
    }
    setAccountDefault(picker.accountId, { buildingName: name });
  };

  const blocks = data?.blocks || [];
  const accountDefaults = data?.accountDefaults || [];

  return (
    <div className="email-signature-editor email-classification-rules">
      <button type="button" className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--email kj-btn--block" onClick={() => setExpanded((v) => !v)}>
        <span className="material-symbols-outlined email-classification-rules__toggle-icon">rule</span>
        <span>{strings.email_classify_rules_title || "Email classification rules"}</span>
        <span className="material-symbols-outlined email-classification-rules__toggle-caret">
          {expanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
        </span>
      </button>

      {expanded && (
        <div className="email-signature-body">
          {/* Account defaults */}
          <div className="email-classification-rules__heading-row">
            <span className="email-classification-rules__heading">
              {strings.email_default_project_per_account || "Default project per account"}
            </span>
            <button
              type="button"
              className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email"
              title={strings.info || "Info"}
              onClick={() => setShowInfo((v) => !v)}
              aria-expanded={showInfo}
            >
              <MaterialIcon icon="info" size={16} color="#b15ec0" />
            </button>
          </div>

          {showInfo && (
            <div className="email-classification-rules__info">
              {strings.email_default_project_info ||
                "When an email account is linked to a specific project, ALL incoming emails from that account are automatically associated to that project — you won't be asked to classify them. Leave it empty to classify each email individually."}
            </div>
          )}

          {accountDefaults.length === 0 ? (
            <div className="email-classification-rules__empty email-classification-rules__empty--spaced">—</div>
          ) : (
            accountDefaults.map((a) => (
              <div key={a.id} className="email-classification-rules__row">
                <span className="email-classification-rules__row-label">
                  {a.email}
                </span>
                <span className={`email-classification-rules__default${a.defaultBuilding ? " email-classification-rules__default--set" : ""}`}>
                  {a.defaultBuilding ? a.defaultBuilding.name : (strings.email_no_default || "None")}
                </span>
                <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email" title={strings.edit || "Edit"} onClick={() => setPicker({ accountId: a.id, query: "", selectedBuildingId: null })}>
                  <MaterialIcon icon="edit" size={16} />
                </button>
                {a.defaultBuilding && (
                  <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--email" title={strings.remove || "Remove"} onClick={() => apply({ action: "setAccountDefault", accountId: a.id, buildingId: null })}>
                    <MaterialIcon icon="close" size={16} />
                  </button>
                )}
              </div>
            ))
          )}

          {/* Spam blocklist */}
          <div className="email-classification-rules__section-title">
            {strings.email_spam_blocklist || "Spam blocklist"}
          </div>
          {blocks.length === 0 ? (
            <div className="email-classification-rules__empty">—</div>
          ) : (
            blocks.map((b) => (
              <div key={b.id} className="email-classification-rules__row">
                <span className="email-classification-rules__row-label">
                  {b.senderEmail}
                </span>
                <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger" title={strings.remove || "Remove"} onClick={() => apply({ action: "removeBlock", id: b.id })}>
                  <MaterialIcon icon="close" size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {picker && (
        <div className="email-attachment-move-overlay" onClick={() => setPicker(null)}>
          <div className="email-attachment-move-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-classification-rules__modal-head">
              <span className="email-classification-rules__modal-title">
                {strings.email_select_project || "Select project"}
              </span>
              <button className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral email-classification-rules__modal-close" onClick={() => setPicker(null)}>
                <MaterialIcon icon="close" size={18} />
              </button>
            </div>
            <ProjectPicker
              value={picker.query}
              buildings={buildings}
              disabled={busy}
              autoFocus
              onChange={(v) => setPicker((p) => ({ ...p, query: v, selectedBuildingId: null }))}
              onSelectExisting={(b) => setPicker((p) => ({ ...p, query: b.name, selectedBuildingId: b.id }))}
              onEnter={submitPicker}
              strings={strings}
            />
            <button
              className="kj-btn kj-btn--primary kj-btn--email kj-btn--block email-classify-btn"
              onClick={submitPicker}
              disabled={busy || !((picker.query || "").trim())}
            >
              <MaterialIcon icon="check" size={16} />
              <span>{strings.save || "Save"}</span>
            </button>
          </div>
        </div>
      )}

      {confirm && picker && (
        <ProjectConfirmModal
          typedName={(picker.query || "").trim()}
          suggestion={confirm.suggestion}
          onUseExisting={() => setAccountDefault(picker.accountId, { buildingId: confirm.suggestion.id })}
          onCreateNew={() => setAccountDefault(picker.accountId, { buildingName: (picker.query || "").trim() })}
          onClose={() => setConfirm(null)}
          strings={strings}
        />
      )}
    </div>
  );
}
